import { useSelector } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { createAxiosInstance } from "api/axiosConfig";

export const useSendChannelMessage = (channelId) => {
  const queryClient = useQueryClient();

  const tenantVal =
    useSelector((state) => state.auth.user?.tenant) ||
    localStorage.getItem("kinesin-tenant");

  const queryKey = ["chatGroup", tenantVal, channelId];

  return useMutation({
    mutationFn: async (message) => {
      const axiosInstance = createAxiosInstance();
      const response = await axiosInstance.post(
        `${tenantVal}/chat/group/${channelId}/message`,
        {
          message,
          createdByStaffId: Number(tenantVal),
        }
      );

      return response.data;
    },
    onMutate: async (message) => {
      await queryClient.cancelQueries({ queryKey });

      const tempId = `temp-${Date.now()}`;
      const optimisticMessage = {
        id: tempId,
        message,
        sentTime: new Date().toISOString(),
        createdByStaffId: Number(tenantVal),
        // Outgoing channel messages don't carry `assignToStaff.tenant`,
        // so we surface ownership through the explicit `_outgoing`
        // marker that chat-messages.jsx checks for status rendering.
        _outgoing: true,
        _status: "sending",
      };

      queryClient.setQueryData(queryKey, (previous) => {
        if (!previous || !Array.isArray(previous.pages)) {
          return {
            pages: [
              {
                messages: [optimisticMessage],
                page: 0,
                size: 20,
                totalElements: 1,
                totalPages: 1,
                hasNext: false,
                hasPrevious: false,
              },
            ],
            pageParams: [0],
          };
        }
        const pages = previous.pages.map((page, index) =>
          index === 0
            ? { ...page, messages: [optimisticMessage, ...(page.messages || [])] }
            : page
        );

        return { ...previous, pages };
      });

      return { tempId };
    },
    onError: (_err, _message, context) => {
      // Don't roll back — mark the optimistic message as failed so the
      // user sees a red retry icon next to it instead of losing the
      // bubble entirely.
      if (!context?.tempId) return;
      queryClient.setQueryData(queryKey, (previous) => {
        if (!previous || !Array.isArray(previous.pages)) return previous;
        const pages = previous.pages.map((page) => ({
          ...page,
          messages: (page.messages || []).map((msg) =>
            msg.id === context.tempId ? { ...msg, _status: "failed" } : msg
          ),
        }));

        return { ...previous, pages };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ["chatGroups"] });
    },
  });
};
